import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  Building2, 
  MessageSquare, 
  Eye, 
  TrendingUp,
  BarChart3
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#0EA5E9', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'];

export default function AnalyticsPage() {
  const { getAuthHeader } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/analytics`, {
        headers: getAuthHeader()
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-navy-200 rounded w-1/4" />
          <div className="grid grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-navy-200 rounded-xl" />
            ))}
          </div>
          <div className="h-80 bg-navy-200 rounded-xl" />
        </div>
      </div>
    );
  }

  const statCards = [
    { 
      title: 'Total Properties', 
      value: analytics?.total_properties || 0, 
      icon: Building2,
      color: 'bg-sky-500'
    },
    { 
      title: 'Total Enquiries', 
      value: analytics?.total_enquiries || 0, 
      icon: MessageSquare,
      color: 'bg-green-500'
    },
    { 
      title: 'Page Views', 
      value: analytics?.total_page_views || 0, 
      icon: Eye,
      color: 'bg-purple-500'
    },
    { 
      title: 'Property Views', 
      value: analytics?.total_property_views || 0, 
      icon: TrendingUp,
      color: 'bg-orange-500'
    },
  ];

  // Format daily views for chart
  const chartData = analytics?.daily_views?.map(dv => ({
    date: new Date(dv.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
    views: dv.views
  })) || [];

  return (
    <div className="p-6 lg:p-8" data-testid="analytics-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-heading font-bold text-navy-900">Analytics</h1>
        <p className="text-navy-600">Track your website and property performance</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => (
          <Card key={stat.title} data-testid={`stat-${stat.title.toLowerCase().replace(' ', '-')}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-navy-600 mb-1">{stat.title}</p>
                  <p className="text-3xl font-bold text-navy-900">{stat.value}</p>
                </div>
                <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        {/* Views Over Time */}
        <Card className="lg:col-span-2" data-testid="views-chart">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-sky-500" />
              Page Views (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    axisLine={{ stroke: '#E2E8F0' }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fill: '#64748B' }}
                    axisLine={{ stroke: '#E2E8F0' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#0F172A', 
                      border: 'none', 
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="views" 
                    stroke="#0EA5E9" 
                    strokeWidth={2}
                    fill="url(#viewsGradient)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Property Type Distribution */}
        <Card data-testid="property-distribution-chart">
          <CardHeader>
            <CardTitle className="text-lg">Property Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {analytics?.property_type_distribution?.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={analytics.property_type_distribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="count"
                      nameKey="type"
                      label={({ type, count }) => `${type}: ${count}`}
                    >
                      {analytics.property_type_distribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-navy-500">
                <p>No properties to display</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Properties */}
        <Card data-testid="top-properties-chart">
          <CardHeader>
            <CardTitle className="text-lg">Top Performing Properties</CardTitle>
          </CardHeader>
          <CardContent>
            {analytics?.top_properties?.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.top_properties} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" tick={{ fontSize: 12, fill: '#64748B' }} />
                    <YAxis 
                      type="category" 
                      dataKey="title" 
                      tick={{ fontSize: 12, fill: '#64748B' }}
                      width={100}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#0F172A', 
                        border: 'none', 
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                    />
                    <Bar dataKey="views" fill="#0EA5E9" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-navy-500">
                <p>No properties to display</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Monthly Summary */}
      <Card data-testid="monthly-summary">
        <CardHeader>
          <CardTitle className="text-lg">This Month's Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="p-4 bg-navy-50 rounded-lg">
              <p className="text-sm text-navy-600 mb-1">Enquiries This Month</p>
              <p className="text-3xl font-bold text-navy-900">{analytics?.enquiries_this_month || 0}</p>
            </div>
            <div className="p-4 bg-navy-50 rounded-lg">
              <p className="text-sm text-navy-600 mb-1">Views This Month</p>
              <p className="text-3xl font-bold text-navy-900">{analytics?.views_this_month || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
